import { useContext } from "react";
import type { LeadKind, PreviewPiece } from "../types";
import type { Words } from "./copy";
import { PageContext } from "./page";
import { MultiBuyOffer, multiBuyWords, offVars } from "./Quantity";
import { openingSize } from "./kinds";
import { BasketContext, pieceKey } from "./selection";

/** One line above the cards of a page of several, saying what they are.
 *
 *  The pieces are independent: they can have one, some or all, so the page
 *  says that before the first of them, rather than leaving three cards each
 *  with an order button to be read as three choices out of which to pick one.
 *  And where more than one in the same parcel is cheaper (the multi-buy),
 *  what that is worth, in the server's own words and numbers (multiBuyWords)
 *  and only where the page takes the payment itself, which is where the
 *  multi-buy applies at all. */
export default function PiecesIntro({
  pieces,
  fallbackKind,
  words: t,
}: {
  pieces: PreviewPiece[];
  fallbackKind: LeadKind;
  words: Words;
}) {
  const page = useContext(PageContext);
  const info = useContext(BasketContext);
  // Every piece that can be chosen, in one tap: what the order sheet did for
  // "together" when it opened with all of them in. Left to them, not done for
  // them: pieces are independent, and nothing is in the order until they say.
  const selectable = pieces.filter((p) => !!p.price?.sizes?.length);
  const allIn = !!info && selectable.every((p) => info.basket.lines.some((l) => l.key === pieceKey("", p.id)));
  const language = page?.language ?? "en";
  // The same offer "Order them together" shows: the first piece that has one.
  const offers = pieces.map((p) => p.price?.multiBuy).filter((m) => !!m);
  const found = offers[0] ?? null;
  const multi =
    found?.endsOn && offers.some((m) => m.usual == null) ? { ...found, usual: undefined } : found;
  const said = page?.checkout && multi ? multiBuyWords(multi, t, language) : null;

  return (
    <div className="stack" style={{ marginBottom: 4 }}>
      <p className="small" style={{ margin: 0 }}>
        {t.t("piecesIntro")}
      </p>
      {info?.on && !allIn && (
        <div>
          <button
            type="button"
            className="btn btn-quiet btn-sm"
            onClick={() => {
              for (const p of selectable) {
                const key = pieceKey("", p.id);
                if (!info.basket.lines.some((l) => l.key === key)) {
                  page?.onPick?.(key, openingSize(p.price?.sizes ?? [], p.kind ?? fallbackKind));
                }
              }
            }}
          >
            {t.t("basketSelectAll")}
          </button>
        </div>
      )}
      {multi && said && (
        <MultiBuyOffer badge={t.t("multiBuyOff", offVars(multi.percent))}>
          {said.dated && (
            <>
              <strong>{said.title}</strong>
              {/* Chinese runs on with no space. */}
              {language === "zh" || language === "zh-hans" ? "" : " "}
            </>
          )}
          {said.together}
        </MultiBuyOffer>
      )}
    </div>
  );
}
